/**
 * Shared atmospheric template for the onboarding/auth flow (spec §6.8):
 * vertical blue gradient, fog layers, original RUST wordmark as inline SVG
 * text treatment, back/close control in the safe top area.
 * Internal to the auth feature — not part of the public contract barrel.
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Banner } from '@/components/ui'
import { BackIcon, CloseIcon } from '@/components/icons'
import { PATHS } from '@/app/router/paths'
import './auth-pages.css'

/** Original RUST wordmark, rendered as inline SVG text (display serif). */
export function RustWordmark({ width = 180 }: { width?: number }) {
  const height = Math.round(width * 0.32)
  return (
    <svg
      className="auth-wordmark"
      width={width}
      height={height}
      viewBox="0 0 180 58"
      role="img"
      aria-label="RUST"
    >
      <text
        x="90"
        y="44"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="40"
        fontFamily="'Playfair Display', Georgia, serif"
        letterSpacing="14"
      >
        RUST
      </text>
    </svg>
  )
}

type AuthLayoutProps = {
  /** Where the top-left control navigates. Omit to hide the control. */
  backTo?: string
  /** 'close' renders an X, 'back' renders a chevron. */
  backKind?: 'back' | 'close'
  backLabel?: string
  children: ReactNode
}

export function AuthLayout({ backTo, backKind = 'back', backLabel = 'Terug', children }: AuthLayoutProps) {
  return (
    <div className="auth-screen">
      <div className="auth-topbar">
        {backTo ? (
          <Link
            to={backTo}
            aria-label={backLabel}
            style={{
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              borderRadius: '50%',
            }}
          >
            {backKind === 'close' ? <CloseIcon size={24} /> : <BackIcon size={24} />}
          </Link>
        ) : (
          <span className="auth-topbar-spacer" aria-hidden="true" />
        )}
        <span className="auth-topbar-spacer" aria-hidden="true" />
      </div>
      <div className="auth-body">{children}</div>
    </div>
  )
}

export function AuthLegalFooter() {
  return (
    <nav className="auth-footer-legal" aria-label="Juridisch en hulp">
      <Link to={PATHS.legal.privacy}>Privacy</Link>
      <Link to={PATHS.legal.terms}>Voorwaarden</Link>
      <Link to={PATHS.legal.help}>Hulp</Link>
    </nav>
  )
}

/**
 * Honest configuration state: Supabase env is missing, so auth cannot work.
 * No fake guest login, no silent fallback.
 */
export function NotConfiguredBanner() {
  return (
    <Banner tone="warning">
      De cloudverbinding is niet ingesteld. Inloggen en registreren zijn daarom nu niet
      beschikbaar. Neem contact op met de beheerder van deze installatie.
    </Banner>
  )
}
